import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Member, Organisation } from "@/lib/types";
import { organisationsKey } from "@/lib/session";
import { Head, Measure } from "@/ui/frame/Page";
import { SettingsSection } from "@/ui/Settings";
import { ProcessingAgreementPanel } from "@/features/org/ProcessingAgreementPanel";
import { Button } from "@/ui/Button";
import { OrgSettingsScreens } from "@/features/org/screens";
import { Field, Input } from "@/ui/Field";
import { Choice } from "@/ui/Choice";
import { Dialog } from "@/ui/Overlay";
import { ErrorState, Skeleton } from "@/ui/States";
import { FormError } from "@/features/auth/AuthCard";
import { useToast } from "@/ui/Toast";
import { CopyField } from "@/ui/Copy";
import { ROLE_LABELS } from "@/features/org/roles";
import { useSession } from "@/lib/session";

export const Route = createFileRoute("/_app/o/$orgId/settings")({
  component: SettingsScreen,
});

function SettingsScreen() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useSession();

  const [displayName, setDisplayName] = useState("");
  const [transferring, setTransferring] = useState(false);

  const organisation = useQuery({
    queryKey: ["organisation", orgId],
    queryFn: ({ signal }) =>
      api.get<{ organisation: Organisation }>(`/organisations/${orgId}`, undefined, signal),
  });

  useEffect(() => {
    if (organisation.data) setDisplayName(organisation.data.organisation.display_name);
  }, [organisation.data]);

  const rename = useMutation({
    mutationFn: () =>
      api.patch<{ organisation: Organisation }>(`/organisations/${orgId}`, {
        display_name: displayName,
      }),
    onSuccess: async () => {
      toast.success("Organisation renamed");
      await queryClient.invalidateQueries({ queryKey: ["organisation", orgId] });
      await queryClient.invalidateQueries({ queryKey: organisationsKey });
    },
  });

  if (organisation.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (organisation.isError) {
    return <ErrorState error={organisation.error} onRetry={() => void organisation.refetch()} />;
  }

  const org = organisation.data!.organisation;
  const isOwner = org.role === "OWNER";
  const canRename = org.role === "OWNER" || org.role === "CO_OWNER";
  const unchanged = displayName.trim() === org.display_name;

  return (
    <div className="flex flex-col gap-8">
      <Measure>
        <Head
          title="Settings"
          description="How this organisation is identified and who owns it."
          screens={<OrgSettingsScreens />}
        />
        <div className="flex flex-col gap-8">
        {/* The name first.
            The processing agreement was at the top because it was added
            last, and it is the longest thing here by a factor of four —
            a page about how an organisation is identified opened with a
            German data-protection contract. The name is what the screen is
            called Settings for. */}
        <SettingsSection
          id="name"
          title="Name"
          description="Shown to everyone in the organisation and used in emails."
        >
          <div className="flex flex-col gap-4">
            <Field label="Display name">
              {({ id }) => (
                <Input
                  id={id}
                  value={displayName}
                  disabled={!canRename}
                  maxLength={60}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              )}
            </Field>
            {rename.error ? <FormError error={rename.error} /> : null}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="t-meta">
                {canRename
                  ? "Changes take effect immediately."
                  : "Only owners and co-owners can rename."}
              </span>
              <Button
                tone="primary"
                size="dense"
                busy={rename.isPending}
                disabled={!canRename || unchanged || displayName.trim().length < 2}
                onClick={() => rename.mutate()}
              >
                Save
              </Button>
            </div>
          </div>
        </SettingsSection>

        <SettingsSection
          id="processing"
          title="Auftragsverarbeitung"
          description="Der Vertrag nach Art. 28 DSGVO zwischen Ihrem Unternehmen und Drumlin, mit Ihren Angaben ausgefüllt."
        >
          <ProcessingAgreementPanel orgId={orgId} />
        </SettingsSection>

        <SettingsSection
          id="identifiers"
          title="Identifiers"
          description="You will need these when using the API or the CLI."
        >
          <div className="flex flex-col gap-4">
            <CopyField label="Organisation ID" value={org.id} />
            <CopyField label="Slug" value={org.slug} />
          </div>
        </SettingsSection>

        {isOwner && (
          <SettingsSection
            id="ownership"
            title="Ownership"
            description="An organisation has exactly one owner. Transferring makes you a co-owner."
          >
            <TransferOwnershipDialog
              orgId={orgId}
              currentUserId={user?.id ?? ""}
              open={transferring}
              onOpenChange={setTransferring}
            />
            <Button tone="secondary" onClick={() => setTransferring(true)}>
              Transfer ownership
            </Button>
          </SettingsSection>
        )}
        </div>
      </Measure>
    </div>
  );
}

function TransferOwnershipDialog({
  orgId,
  currentUserId,
  open,
  onOpenChange,
}: {
  orgId: string;
  currentUserId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [userId, setUserId] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const members = useQuery({
    queryKey: ["members", orgId],
    queryFn: ({ signal }) =>
      api.get<{ members: Member[] }>(`/organisations/${orgId}/members`, undefined, signal),
    enabled: open,
  });

  const transfer = useMutation({
    mutationFn: () => api.post<void>(`/organisations/${orgId}/transfer-ownership`, { user_id: userId }),
    onSuccess: async () => {
      toast.success("Ownership transferred", "You are now a co-owner.");
      await queryClient.invalidateQueries({ queryKey: ["members", orgId] });
      await queryClient.invalidateQueries({ queryKey: ["organisation", orgId] });
      await queryClient.invalidateQueries({ queryKey: organisationsKey });
      onOpenChange(false);
    },
  });

  const candidates =
    members.data?.members.filter((member) => member.user_id !== currentUserId) ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Transfer ownership"
      description="The new owner gains full control, including the ability to delete this organisation. You become a co-owner."
      footer={
        <>
          <Button tone="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            tone="danger"
            busy={transfer.isPending}
            disabled={!userId || confirmation !== "transfer"}
            onClick={() => transfer.mutate()}
          >
            Transfer ownership
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {candidates.length === 0 ? (
          <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
            There is nobody else in this organisation yet. Invite a colleague first.
          </p>
        ) : (
          <>
            <Field label="New owner" required>
              {({ id }) => (
                <Choice
                  id={id}
                  value={userId}
                  onValueChange={setUserId}
                  placeholder="Choose a member"
                  options={candidates.map((member) => ({
                    value: member.user_id,
                    label: member.display_name || member.email,
                    description: `${member.email} · ${ROLE_LABELS[member.role]}`,
                  }))}
                />
              )}
            </Field>

            <Field
              label="Type transfer to confirm"
              required
              hint="This cannot be undone without the new owner transferring it back."
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="transfer"
                  autoComplete="off"
                />
              )}
            </Field>
          </>
        )}

        {transfer.error ? <FormError error={transfer.error} /> : null}
      </div>
    </Dialog>
  );
}
